import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Row, Col, Form, OverlayTrigger, Spinner, Tooltip as RbTooltip } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { MdFilterList, MdAdd, MdDelete, MdEdit, MdDownload, MdFileDownload, MdRemoveRedEye } from "react-icons/md";
import { Tooltip } from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import * as XLSX from 'xlsx';
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from '../../components/common/AppDataGrid';

const MAX_AMOUNT = 1e10;
const isFiniteNum = (v) => Number.isFinite(Number(v));
const nz = (v) => (isFiniteNum(v) ? Number(v) : 0);
const clampMoney = (v) => {
    const n = nz(v);
    if (n < 0 || n > MAX_AMOUNT) return 0;
    return n;
};

const validMonth = (m) => Number.isInteger(Number(m)) && Number(m) >= 1 && Number(m) <= 12;
const validYear = (y) => Number.isInteger(Number(y)) && String(y).length === 4;

const ViewPayroll = () => {
    const [payrollData, setPayrollData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // NEW: gate for month/year fetch completion
    const [hasLoadedMonthData, setHasLoadedMonthData] = useState(false);

    const [showFilterModal, setShowFilterModal] = useState(false);
    const [filter, setFilter] = useState({ emp_id: '', action: 'read' });
    const [monthList, setMonthList] = useState([]);
    const [processingDates, setProcessingDates] = useState([]);
    const [isTodayProcessingDate, setIsTodayProcessingDate] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [salaryComponents, setSalaryComponents] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [showEmployeeModal, setShowEmployeeModal] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [isPayrollAlreadyDoneForMonth, setIsPayrollAlreadyDoneForMonth] = useState(false);
    const [selectedSalaryComp, setSelectedSalaryComp] = useState({ component_id: "", amount: "" });
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewModal, setViewModal] = useState({
        emp: null,
        earnings: [],
        deductions: [],
        loading: false
    });
    const [academicYears, setAcademicYears] = useState([]);
    const [derivedAcademicYearId, setDerivedAcademicYearId] = useState('');
    const now = new Date();
    const defaultYear = String(now.getFullYear());
    const defaultMonth = String(now.getMonth() + 1);
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
    const [selectedYear, setSelectedYear] = useState(defaultYear);
    const [componentForm, setComponentForm] = useState({
        amount: '',
        component_type_id: '',
        salary_component_id: '',
        reason: ''
    });
    const [employeeComponents, setEmployeeComponents] = useState([]);
    const [isLoadingComponents, setIsLoadingComponents] = useState(false);
    const [deletingCompId, setDeletingCompId] = useState(null);
    const [componentsByEmp, setComponentsByEmp] = useState({});
    const [loadingExpanded, setLoadingExpanded] = useState({});
    const [form, setForm] = useState({ payroll_month: '' });
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const userData = sessionStorage.getItem('user');
    const userObj = userData ? JSON.parse(userData) : {};

    useEffect(() => {
        fetchEmployees();
        fetchSalaryComponents();
        fetchMonths();
        fetchAcademicYears();
        fetchProcessingDates();
    }, []);

    useEffect(() => {
        if (!selectedYear) setSelectedYear(defaultYear);
        if (!selectedMonth) setSelectedMonth(defaultMonth);
    }, [monthList]);

    useEffect(() => {
        if (!selectedMonth || !selectedYear) {
            setPayrollData([]);
            setDerivedAcademicYearId('');
            setHasLoadedMonthData(false); // reset gate when selection is empty
            return;
        }
        if (!academicYears || academicYears.length === 0) {
            return;
        }

        const ayId = resolveDerivedAcademicYearId(selectedMonth, selectedYear, academicYears);
        setDerivedAcademicYearId(ayId);
        fetchPayrollDataByMonthYear();
        checkPayrollAlreadyProcessedForMonthYear();
    }, [selectedMonth, selectedYear, academicYears]);

    const isSelectedMonthYearToday = useMemo(() => {
        const t = new Date();
        return Number(selectedMonth) === (t.getMonth() + 1) &&
            Number(selectedYear) === t.getFullYear();
    }, [selectedMonth, selectedYear]);

    const fetchAcademicYears = async () => {
        try {
            const response = await axios.post(`${baseUrl}/AcademicYear/`, {
                action: 'READ',
                school_id: userObj.school_id
            });
            setAcademicYears(response.data || []);
        } catch (error) {
            console.error('Error fetching academic years:', error);
            toast.error("Failed to load academic years.");
        }
    };

    const fetchEmployees = async () => {
        setIsLoading(true);
        try {
            const response = await axios.post(`${baseUrl}/employeesalary/read`, {
                action: 'READ',
                emp_id: 0,
                school_id: userObj.school_id
            });

            const rawList = response.data || [];
            const filtered = rawList.filter(item =>
                (item.is_current === 'Current' || item.is_current === 'Y') &&
                (item.emp_status ? String(item.emp_status).toUpperCase() !== 'RESIGNED' : true)
            );
            setEmployees(filtered);

            const expandedComponentMap = {};
            for (const item of rawList) {
                const empId = item.emp_id;
                const salaryEntry = item.salary?.find(s => s.is_current === 'Y') || item.salary?.[0];
                const components = (salaryEntry?.components || []).map(component => ({
                    id: component.employee_salary_component_id || component.salary_component_id || Math.random(),
                    salary_component_name: component.component_name ?? "Component",
                    component_type_name: component.component_type_name ?? "",
                    amount: clampMoney(component.component_amount ?? 0)
                }));
                expandedComponentMap[empId] = components;
            }
            setComponentsByEmp(expandedComponentMap);
        } catch (error) {
            console.error('Error fetching employees:', error);
            toast.error("Failed to load employee salaries.");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPayrollDataByMonthYear = async () => {
        if (!selectedMonth || !selectedYear) return;
        if (!validMonth(selectedMonth) || !validYear(selectedYear)) {
            toast.error("Invalid period selected.");
            return;
        }

        setIsLoading(true);
        setHasLoadedMonthData(false); // NEW: block grid until this fetch finishes
        try {
            const response = await axios.post(`${baseUrl}/employeesalary/readmonthwise`, {
                school_id: userObj.school_id,
                month_id: parseInt(selectedMonth),
                year: parseInt(selectedYear)
            });

            const clean = (response.data || []).map(r => ({
                ...r,
                monthly_gross: clampMoney(r.monthly_gross),
                monthly_net: clampMoney(r.monthly_net ?? r.monthly_net_salary),
                monthly_net_salary: clampMoney(r.monthly_net_salary ?? r.monthly_net),
                monthly_deduction: clampMoney(r.monthly_deduction ?? r.monthly_deductions),
                monthly_deductions: clampMoney(r.monthly_deductions ?? r.monthly_deduction),
            }));

            setPayrollData(clean);
        } catch (error) {
            console.error('Error fetching payroll data by month/year:', error);
            toast.error('Error fetching payroll data');
            setPayrollData([]);
        } finally {
            setIsLoading(false);
            setHasLoadedMonthData(true); // NEW: now grid can render
        }
    };

    const fetchMonths = async () => {
        try {
            const response = await axios.post(baseUrl + '/months/', { action: 'READ' });
            setMonthList(response.data || []);
        } catch (error) {
            toast.error("Failed to load months.");
        }
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setForm(prev => ({ ...prev, [id]: value }));
    };

    const fetchSalaryComponents = async () => {
        try {
            const response = await axios.post(`${baseUrl}/salarycomponent/`, {
                action: 'READ',
                school_id: userObj.school_id
            });
            setSalaryComponents(response.data || []);
        } catch (error) {
            console.error('Error fetching salary components:', error);
        }
    };

    const fetchEmpDetailsForMonthYear = async (empId, month, year) => {
        const resp = await axios.post(`${baseUrl}/employeesalary/details`, {
            emp_id: empId,
            month: Number(month),
            year: Number(year)
        });

        const salaryData = resp.data?.salary?.[0] || {};
        const sComps = salaryData.components || [];
        const bComps = resp.data?.bonus || [];
        const combined = [...sComps, ...bComps].map(item => ({
            id: item.employee_salary_component_id || item.employee_bonus_id || Math.random(),
            employee_name: salaryData.emp_name || "",
            component_type_name: item.component_type_name,
            salary_component_name: item.salary_component_name || item.component_name,
            amount: clampMoney(item.component_amount || item.amount),
            created_date: item.createddate
        }));

        const earnings = combined.filter(c => String(c.component_type_name).toUpperCase() === 'EARNING');
        const deductions = combined.filter(c => String(c.component_type_name).toUpperCase() === 'DEDUCTION');

        const meta = {
            emp_name: salaryData.emp_name || '',
            emp_id: salaryData.emp_id || empId
        };

        return { earnings, deductions, combined, meta };
    };

    const fetchEmployeeComponents = async (empId) => {
        setIsLoadingComponents(true);
        try {
            const now = new Date();
            const month = now.getMonth() + 1;
            const year = now.getFullYear();

            const response = await axios.post(`${baseUrl}/employeesalary/details`, {
                emp_id: empId,
                month,
                year
            });

            const salaryData = response.data.salary?.[0] || {};
            const sComps = salaryData.components || [];
            const bComps = response.data.bonus || [];

            const combinedComponents = [...sComps, ...bComps].map(item => ({
                id: item.employee_salary_component_id || item.employee_bonus_id,
                employee_name: salaryData.emp_name || "",
                component_type_name: item.component_type_name,
                salary_component_name: item.salary_component_name || item.component_name,
                amount: clampMoney(item.component_amount || item.amount),
                created_date: item.createddate
            }));

            setEmployeeComponents(combinedComponents);
        } catch (error) {
            console.error('Error fetching employee bonuses:', error);
            toast.error("Failed to fetch components.");
        } finally {
            setIsLoadingComponents(false);
        }
    };

    const fetchProcessingDates = async () => {
        const toLocalYMD = (d) => {
            const pad = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        };

        const parseProcessingDate = (raw) => {
            if (!raw) return null;
            const s = String(raw).trim();

            if (/^\d{4}[-/]\d{2}[-/]\d{2}/.test(s)) {
                const d = new Date(s);
                return isNaN(d.getTime()) ? null : d;
            }

            const m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
            if (m) {
                const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
                return isNaN(d.getTime()) ? null : d;
            }

            const d = new Date(s);
            return isNaN(d.getTime()) ? null : d;
        };

        try {
            const response = await axios.post(`${baseUrl}/payrollschedule/read`, {
                action: 'READ',
                school_id: userObj.school_id,
            });

            const dates = (response.data || [])
                .map((item) => {
                    const d = parseProcessingDate(item.processing_date);
                    return d ? toLocalYMD(d) : null;
                })
                .filter(Boolean);

            setProcessingDates(dates);

            const today = toLocalYMD(new Date());
            setIsTodayProcessingDate(dates.includes(today));
        } catch (error) {
            console.error("Failed to fetch processing dates", error);
            setIsTodayProcessingDate(false);
        }
    };

    const checkPayrollAlreadyProcessedForMonthYear = async () => {
        if (!selectedMonth || !selectedYear) {
            setIsPayrollAlreadyDoneForMonth(false);
            return;
        }
        try {
            const response = await axios.post(`${baseUrl}/employeesalary/readmonthwise`, {
                school_id: userObj.school_id,
                month_id: parseInt(selectedMonth),
                year: parseInt(selectedYear)
            });

            const hasProcessedPayroll = (response.data || []).some(record => {
                const hasValidPayrollId = record.employee_salary_payroll_id !== null &&
                    record.employee_salary_payroll_id !== undefined &&
                    record.employee_salary_payroll_id !== "";

                const hasCreatedDate = record.createddate !== null &&
                    record.createddate !== undefined;

                return hasValidPayrollId || hasCreatedDate;
            });

            setIsPayrollAlreadyDoneForMonth(hasProcessedPayroll);
        } catch (error) {
            console.error("Error checking payroll status:", error);
            setIsPayrollAlreadyDoneForMonth(false);
        }
    };

    const resolveDerivedAcademicYearId = (monthStr, yearStr, academicYears) => {
        const m = parseInt(monthStr, 10);
        const y = parseInt(yearStr, 10);
        if (!m || !y || !Array.isArray(academicYears) || academicYears.length === 0) return '';

        const sampleDate = new Date(y, m - 1, 1);
        const withDates = academicYears.find(ay => {
            const s = ay.start_date ? new Date(ay.start_date) : null;
            const e = ay.end_date ? new Date(ay.end_date) : null;
            return s && e && sampleDate >= s && sampleDate <= e;
        });
        if (withDates?.academic_year_id) return String(withDates.academic_year_id);

        const withMonthFields = academicYears.find(ay => {
            const sm = parseInt(ay.start_month, 10);
            const sy = parseInt(ay.start_year, 10);
            const em = parseInt(ay.end_month, 10);
            const ey = parseInt(ay.end_year, 10);
            if (!sm || !sy || !em || !ey) return false;
            const startD = new Date(sy, sm - 1, 1);
            const endD = new Date(ey, em - 1, 1);
            return sampleDate >= startD && sampleDate <= endD;
        });
        if (withMonthFields?.academic_year_id) return String(withMonthFields.academic_year_id);
        const startYear = m >= 4 ? y : y - 1;
        const endYear = startYear + 1;
        const expectedName = `${startYear}-${endYear}`.replace(/\s/g, '');
        const byName = academicYears.find(ay =>
            String(ay.academic_year_name || '').replace(/\s/g, '') === expectedName
        );

        return byName?.academic_year_id ? String(byName.academic_year_id) : '';
    };

    const handleRunPayroll = async () => {
        const t = new Date();
        const isCurrentSelection =
            Number(selectedMonth) === (t.getMonth() + 1) &&
            Number(selectedYear) === t.getFullYear();

        if (!isCurrentSelection) {
            toast.error("You can only run payroll for the current month on a processing date.");
            return;
        }
        const ayIdNum = Number(derivedAcademicYearId);
        if (!selectedMonth || !selectedYear) {
            toast.error("Please select Month and Year first");
            return;
        }
        if (!validMonth(selectedMonth) || !validYear(selectedYear)) {
            toast.error("Invalid Month/Year.");
            return;
        }
        if (!Number.isInteger(ayIdNum) || ayIdNum <= 0) {
            toast.error("Unable to resolve Academic Year from the selected Month & Year.");
            return;
        }
        if (!isTodayProcessingDate) {
            toast.error("Today is not a configured payroll processing date.");
            return;
        }
        if (isPayrollAlreadyDoneForMonth) {
            toast.info("Payroll has already been processed for this month.");
            return;
        }

        try {
            setIsProcessing(true);

            const submittedEmpIds = new Set(
                (payrollData || [])
                    .filter(r => isPayrollSubmitted(r))
                    .map(r => r.emp_id)
            );

            const monthNum = Number(selectedMonth);
            const empIdsSeen = new Set();

            const rows = [];
            for (const employee of employees) {
                const empId = employee.emp_id;

                if (!empId || submittedEmpIds.has(empId)) continue;
                if (empIdsSeen.has(empId)) {
                    continue;
                }
                empIdsSeen.add(empId);

                const row = {
                    emp_id: empId,
                    ctc: clampMoney(employee.ctc),
                    gross_salary: clampMoney(employee.gross_salary),
                    net_salary: clampMoney(employee.net_salary),
                    deductions: clampMoney(employee.deductions_total ?? employee.deductions),
                    taxes: clampMoney(employee.taxes_total ?? employee.taxes),
                    monthly_deduction: clampMoney(employee.monthly_deductions ?? employee.monthly_deduction),
                    monthly_gross: clampMoney(employee.monthly_gross),
                    monthly_net_salary: clampMoney(employee.monthly_net ?? employee.monthly_net_salary),
                    school_id: userObj.school_id,
                    createdby: userObj.username || 'system',
                    month_id: monthNum,
                    academic_year_id: ayIdNum,
                };

                const expectedNet = Math.max(0, nz(row.monthly_gross) - nz(row.monthly_deduction));
                if (Math.abs(expectedNet - nz(row.monthly_net_salary)) > 0.5) {
                    toast.warn(`Net mismatch for ${employee.emp_name}. Will use server rules.`);
                }
                const numericKeys = [
                    'ctc', 'gross_salary', 'net_salary', 'deductions', 'taxes',
                    'monthly_deduction', 'monthly_gross', 'monthly_net_salary'
                ];
                const invalidKey = numericKeys.find(k => !isFiniteNum(row[k]));
                if (invalidKey) {
                    toast.error(`Invalid number for ${employee.emp_name} (${invalidKey}). Skipped.`);
                    continue;
                }

                rows.push(row);
            }

            if (rows.length === 0) {
                toast.info("Nothing to process. All valid employees already submitted or invalid.");
                setIsProcessing(false);
                return;
            }

            const payload = {
                school_id: userObj.school_id,
                createdby: userObj.username || 'system',
                month_id: monthNum,
                academic_year_id: ayIdNum,
                rows
            };

            await axios.post(`${baseUrl}/employeesalary/bulk`, payload);

            toast.success("Payroll processed successfully!");
            await fetchPayrollDataByMonthYear();
            await checkPayrollAlreadyProcessedForMonthYear();
        } catch (error) {
            console.error('Error processing payroll:', error);
            console.error('Error response:', error.response?.data);
            toast.error('Error processing payroll: ' + (error.response?.data?.message || error.message));
        } finally {
            setIsProcessing(false);
        }
    };

    const fmtAmt = (amount) => {
        const num = clampMoney(amount);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(num);
    };

    const handleOpenViewModal = async (employeeRow) => {
        if (!employeeRow?.emp_id) return;
        setShowViewModal(true);
        setViewModal({ emp: employeeRow, earnings: [], deductions: [], loading: true });

        try {
            const { earnings, deductions } = await fetchEmpDetailsForMonthYear(
                employeeRow.emp_id,
                selectedMonth,
                selectedYear
            );
            setViewModal((s) => ({ ...s, earnings, deductions, loading: false }));
        } catch (err) {
            console.error("Failed to load components for view:", err);
            toast.error("Failed to load components.");
            setViewModal((s) => ({ ...s, loading: false }));
        }
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setViewModal({ emp: null, earnings: [], deductions: [], loading: false });
    };

    const handleDownloadExcel = () => {
        if (!payrollData || payrollData.length === 0) {
            toast.error("No data available for download");
            return;
        }
        if (!validMonth(selectedMonth) || !validYear(selectedYear)) {
            toast.error("Select a valid Month & Year to export.");
            return;
        }

        const exportRows = (payrollData || []).map(r => ({
            'Employee Name': r.emp_name,
            'Month': Number(selectedMonth),
            'Year': Number(selectedYear),
            'Monthly Gross': clampMoney(r.monthly_gross),
            'Monthly Deductions': clampMoney(r.monthly_deduction ?? r.monthly_deductions),
            'Monthly Net': clampMoney(r.monthly_net_salary ?? r.monthly_net),
            'Processed?': (isPayrollSubmitted(r) ? 'Y' : 'N')
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Payroll');

        const fileName = `Payroll_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        toast.success("Excel file downloaded successfully!");
    };

    const handleDownloadPayslip = async (employee) => {
        try {
            const empId = employee?.emp_id;
            if (!empId) {
                toast.error('Employee not found for payslip.');
                return;
            }

            const { earnings, deductions, meta } = await fetchEmpDetailsForMonthYear(empId, selectedMonth, selectedYear);

            const monthName = getMonthName(selectedMonth);
            const year = selectedYear;

            const grossEarnings = earnings.reduce((s, it) => s + clampMoney(it.amount), 0);
            const grossDeductions = deductions.reduce((s, it) => s + clampMoney(it.amount), 0);
            const netSalary = Math.max(0, grossEarnings - grossDeductions);

            const numberToWords = (num) => {
                const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];
                const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
                const teens = ['TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];

                if (num === 0) return 'ZERO';

                let n = Math.floor(Math.abs(num));
                let result = '';
                if (n >= 100000) {
                    result += ones[Math.floor(n / 100000)] + ' LAKH ';
                    n %= 100000;
                }
                if (n >= 1000) {
                    if (Math.floor(n / 1000) >= 10) {
                        if (Math.floor(n / 1000) < 20) {
                            result += teens[Math.floor(n / 1000) - 10] + ' THOUSAND ';
                        } else {
                            result += tens[Math.floor(n / 10000)] + ' ';
                            if (Math.floor(n / 1000) % 10 !== 0) {
                                result += ones[Math.floor(n / 1000) % 10] + ' ';
                            }
                            result += 'THOUSAND ';
                        }
                    } else {
                        result += ones[Math.floor(n / 1000)] + ' THOUSAND ';
                    }
                    n %= 1000;
                }
                if (n >= 100) {
                    result += ones[Math.floor(n / 100)] + ' HUNDRED ';
                    n %= 100;
                }
                if (n >= 20) {
                    result += tens[Math.floor(n / 10)] + ' ';
                    if (n % 10 !== 0) {
                        result += ones[n % 10] + ' ';
                    }
                } else if (n >= 10) {
                    result += teens[n - 10] + ' ';
                } else if (n > 0) {
                    result += ones[n] + ' ';
                }

                return result.trim() + ' ONLY';
            };

            const maxRows = Math.max(earnings.length, deductions.length);
            const rowsHtml = Array.from({ length: maxRows }).map((_, i) => {
                const e = earnings[i];
                const d = deductions[i];
                return `
          <tr>
            <td>${e ? (e.salary_component_name || 'Earning') : ''}</td>
            <td class="amount">${e ? (Number(clampMoney(e.amount))).toLocaleString() : ''}</td>
            <td>${d ? (d.salary_component_name || 'Deduction') : ''}</td>
            <td class="amount">${d ? (Number(clampMoney(d.amount))).toLocaleString() : ''}</td>
          </tr>
        `;
            }).join('');

            const payslipHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Payslip - ${meta.emp_name || employee.emp_name || ''}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; background: white; }
          .payslip { max-width: 800px; margin: 0 auto; border: 2px solid #000; }
          .header { text-align: center; padding: 20px; border-bottom: 2px solid #000; }
          .employee-info { padding: 15px; border-bottom: 1px solid #000; }
          .payslip-title { font-weight: bold; margin-top: 8px; }
          .salary-table { width: 100%; border-collapse: collapse; }
          .salary-table th, .salary-table td {
            border: 1px solid #000; padding: 8px; text-align: left;
          }
          .salary-table th { background-color: #f5f5f5; font-weight: bold; }
          .amount { text-align: right; }
          .net-salary { padding: 15px; border-top: 2px solid #000; }
          .signature { padding: 20px; font-size: 12px; text-align: center; }
        </style>
      </head>
      <body>
        <div class="payslip">
          <div class="employee-info">
            <div style="display: flex; justify-content: space-between;">
              <div><strong>Employee Name: ${meta.emp_name || employee.emp_name || ''}</strong></div>
              
            </div>
            <div class="payslip-title">Payslip for the month of ${monthName} ${year}</div>
          </div>

          <table class="salary-table">
            <thead>
              <tr>
                <th>Earnings</th>
                <th class="amount">Amount (₹)</th>
                <th>Deductions</th>
                <th class="amount">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
              <tr style="font-weight: bold; background-color: #f5f5f5;">
                <td>Gross Earnings</td>
                <td class="amount">${grossEarnings.toLocaleString()}</td>
                <td>Gross Deductions</td>
                <td class="amount">${grossDeductions.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>

          <div class="net-salary">
            <div style="font-size: 16px; font-weight: bold;">
              Net Salary: ${netSalary.toLocaleString()}
            </div>
            <div style="font-size: 12px; margin-top: 5px;">
              (${numberToWords(netSalary)})
            </div>
          </div>

          <div class="signature">
            This is computer generated payslip hence no signature is required
          </div>
        </div>
      </body>
      </html>
      `;

            const blob = new Blob([payslipHTML], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Payslip_${meta.emp_name || employee.emp_name || 'Employee'}_${monthName}_${year}.html`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success(`Payslip downloaded for ${meta.emp_name || employee.emp_name}`);
        } catch (err) {
            console.error('Payslip generation failed:', err);
            toast.error('Failed to generate payslip.');
        }
    };

    const getMonthName = (monthNumber) => {
        const monthNames = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        const idx = Number(monthNumber) - 1;
        return monthNames[idx] || "Unknown";
    };

    const handleEmployeeEdit = (employee) => {
        setSelectedEmployee(employee);
        setComponentForm({
            amount: '',
            component_type_id: '',
            salary_component_id: '',
            reason: ''
        });
        fetchEmployeeComponents(employee.emp_id);
        setShowEmployeeModal(true);
    };

    const handleCloseEmployeeModal = () => {
        setShowEmployeeModal(false);
        setSelectedEmployee(null);
        setEmployeeComponents([]);
        setComponentForm({
            amount: '',
            component_type_id: '',
            salary_component_id: '',
            reason: ''
        });
    };

    const handleComponentFormChange = (e) => {
        const { name, value } = e.target;
        setComponentForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAddComponent = async () => {
        const compId = String(componentForm.salary_component_id || '').trim();
        const amount = Number(componentForm.amount);

        if (!compId || !isFiniteNum(amount) || amount <= 0) {
            toast.error("Please select a component and enter a positive amount.");
            return;
        }
        if (amount > MAX_AMOUNT) {
            toast.error("Amount is unrealistically large.");
            return;
        }

        const selectedComp = (salaryComponents || []).find(
            c => String(c.salary_component_id ?? c.component_id ?? c.id) === compId
        );
        if (!selectedComp) {
            toast.error("Invalid component selected.");
            return;
        }

        const component_type_id = selectedComp?.component_type_id;
        if (!component_type_id) {
            toast.error("Component type not found.");
            return;
        }

        const nameToAdd = (selectedComp.component_name ?? selectedComp.salary_component_name ?? '').toLowerCase();
        const dup = (employeeComponents || []).some(ec =>
            (ec.salary_component_name || ec.component_name || '').toLowerCase() === nameToAdd
        );
        if (dup) {
            toast.warn("This component already exists for the employee.");
            return;
        }

        const payload = {
            action: 'CREATE',
            emp_id: selectedEmployee.emp_id,
            salary_component_id: parseInt(compId),
            component_type_id: parseInt(component_type_id),
            amount: clampMoney(amount),
            reason: (componentForm.reason || '').trim() || null,
            createdby: userObj.username,
            lastmodifiedby: userObj.username,
            school_id: userObj.school_id,
        };

        try {
            await axios.post(`${baseUrl}/employeebonus`, payload);

            toast.success("Bonus component added successfully");
            setComponentForm({ salary_component_id: '', amount: '', component_type_id: '', reason: '' });
            setSelectedSalaryComp({ component_id: '' });

            fetchEmployeeComponents(selectedEmployee.emp_id);
        } catch (error) {
            console.error("Error adding bonus:", error);
            toast.error("Failed to add bonus component.");
        }
    };

    const handleDeleteComponent = async (componentId) => {
        try {
            setDeletingCompId(componentId);
            await axios.post(`${baseUrl}/employeebonus`, {
                action: 'DELETE',
                employee_bonus_id: componentId
            });

            toast.success("Bonus component deleted successfully");
            fetchEmployeeComponents(selectedEmployee.emp_id);
        } catch (error) {
            console.error("Error deleting component:", error);
            toast.error("Failed to delete bonus component.");
        } finally {
            setDeletingCompId(null);
        }
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleShowFilterModal = () => setShowFilterModal(true);
    const handleCloseFilterModal = () => setShowFilterModal(false);

    const handleFilterSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!selectedMonth || !selectedYear) {
                toast.error("Please select month and year first");
                return;
            }
            if (!validMonth(selectedMonth) || !validYear(selectedYear)) {
                toast.error("Invalid period.");
                return;
            }

            const response = await axios.post(`${baseUrl}/employeesalary/readmonthwise`, {
                school_id: userObj.school_id,
                month_id: Number(selectedMonth),
                year: Number(selectedYear)
            });

            const cleaned = (response.data || []).map(r => ({
                ...r,
                monthly_gross: clampMoney(r.monthly_gross),
                monthly_net_salary: clampMoney(r.monthly_net_salary ?? r.monthly_net),
                monthly_deduction: clampMoney(r.monthly_deduction ?? r.monthly_deductions),
            }));

            const nameQuery = (filter.emp_id || '').trim().toLowerCase();
            const filteredByName = nameQuery
                ? cleaned.filter(item => (item.emp_name || '').toLowerCase().includes(nameQuery))
                : cleaned;

            setPayrollData(filteredByName);
            setShowFilterModal(false);
        } catch (error) {
            console.error("Error filtering data:", error);
            toast.error("Failed to filter data.");
        }
    };

    const isPayrollSubmitted = (employee) => {
        if (!selectedMonth || !selectedYear || payrollData.length === 0) {
            return false;
        }

        const employeePayroll = payrollData.find(p => p.emp_id === employee.emp_id);

        if (employeePayroll) {
            const hasPayrollId = employeePayroll.employee_salary_payroll_id !== null &&
                employeePayroll.employee_salary_payroll_id !== undefined &&
                employeePayroll.employee_salary_payroll_id !== "";

            const hasCreatedDate = employeePayroll.createddate !== null &&
                employeePayroll.createddate !== undefined;

            const hasCreatedBy = employeePayroll.createdby !== null &&
                employeePayroll.createdby !== undefined;

            return hasPayrollId || hasCreatedDate || hasCreatedBy;
        }
        return false;
    };

    const searchableColumns = [
        (row) => row.emp_name,
        (row) => row.ctc,
        (row) => row.gross_salary,
        (row) => row.net_salary
    ];

    const filteredRecords = useMemo(() => {
        // NEW: never show employees fallback; wait for month data
        const list = hasLoadedMonthData ? (payrollData || []) : [];
        if (!searchQuery) return list;
        return (list || []).filter((item) =>
            searchableColumns.some((selector) => {
                const value = selector(item);
                const stringValue = String(value || "").toUpperCase().replace(/[-\s]+/g, '');
                const normalizedQuery = searchQuery.toUpperCase().replace(/[-\s]+/g, '');
                return stringValue.includes(normalizedQuery);
            })
        );
    }, [searchQuery, payrollData, hasLoadedMonthData]);

    const columns = [
        {
            field: "emp_name",
            headerName: "Employee Name",
            flex: 1.2,
            renderCell: (params) => (
                <Tooltip title={params.value || ''}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            )
        },
        {
            field: "monthly_gross",
            headerName: "Monthly Gross",
            flex: 0.8,
            valueGetter: (params) => clampMoney(params.row?.monthly_gross),
            renderCell: (params) => (
                <Tooltip title={params.row?.monthly_gross}>
                    <span className="cell-wrap">{fmtAmt(params.row?.monthly_gross)}</span>
                </Tooltip>
            )
        },
        {
            field: "monthly_net",
            headerName: "Monthly Net",
            flex: 0.8,
            valueGetter: (params) => clampMoney(params.row?.monthly_net_salary ?? params.row?.monthly_net),
            renderCell: (params) => {
                const v = clampMoney(params.row?.monthly_net_salary ?? params.row?.monthly_net);
                return (
                    <Tooltip title={v}>
                        <span className="cell-wrap">{fmtAmt(v)}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: "monthly_deduction",
            headerName: "Monthly Deductions",
            flex: 0.8,
            valueGetter: (params) => clampMoney(params.row?.monthly_deduction ?? params.row?.monthly_deductions),
            renderCell: (params) => {
                const v = clampMoney(params.row?.monthly_deduction ?? params.row?.monthly_deductions);
                return (
                    <Tooltip title={v}>
                        <span className="cell-wrap">{fmtAmt(v)}</span>
                    </Tooltip>
                );
            }
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row || {};
                const submitted = isPayrollSubmitted(row);
                return (
                    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                        <Button
                            size="sm"
                            variant="link"
                            className="p-0"
                            onClick={() => handleOpenViewModal(row)}
                            title="View Components"
                        >
                            <MdRemoveRedEye size={18} />
                        </Button>
                        {submitted ? (
                            <>
                                <span>Payroll Submitted</span>
                                <Button
                                    size="sm"
                                    variant="link"
                                    className="p-0 text-success"
                                    onClick={() => handleDownloadPayslip(row)}
                                    title="Download Payslip"
                                >
                                    <MdFileDownload size={18} />
                                </Button>
                            </>
                        ) : (
                            <Button
                                size="sm"
                                variant="link"
                                className="p-0 text-primary"
                                onClick={() => handleEmployeeEdit(row)}
                                title="Edit"
                            >
                                <MdEdit size={16} />
                            </Button>
                        )}
                    </div>
                );
            }
        }
    ];

    const renderDetailPanel = (params) => {
        const data = params.row || {};
        const empId = data.emp_id;

        const compList = componentsByEmp[empId] || [];
        const E = compList.filter((it) => String(it.component_type_name).toUpperCase() === "EARNING");
        const D = compList.filter((it) => String(it.component_type_name).toUpperCase() === "DEDUCTION");
        const rowsCnt = Math.max(E.length, D.length);

        const tableS = { width: "100%", borderCollapse: "collapse" };
        const thS = { padding: "0.4rem 0.6rem", fontSize: "0.85rem", fontWeight: 600, border: 0 };
        const thGroup = {
            ...thS,
            background: "#0d6efd",
            color: "#fff",
            textAlign: "center",
            borderRadius: 4,
        };
        const tdS = { padding: "0.4rem 0.6rem", fontSize: "0.85rem", border: 0 };
        const actS = { width: 64, minWidth: 64, whiteSpace: "nowrap" };
        const sep = { borderLeft: "1px solid var(--bs-border-color, #dee2e6)" };

        const renderCells = (item, sideIdx) => {
            const leftCellStyle = { ...tdS, ...(sideIdx > 0 ? sep : {}) };

            if (!item) {
                return (
                    <>
                        <td style={leftCellStyle}></td>
                        <td className="text-end" style={tdS}></td>
                        <td style={{ ...tdS, ...actS }}></td>
                    </>
                );
            }

            const label = item.salary_component_name || item.component_name || "Component";
            return (
                <>
                    <td style={leftCellStyle}>{label}</td>
                    <td className="text-end" style={tdS}>{fmtAmt(item.amount)}</td>
                    <td style={{ ...tdS, ...actS }}></td>
                </>
            );
        };

        return (
            <div style={{ padding: 16, background: "#fafafa", width: "100%" }}>
                {loadingExpanded[empId] ? (
                    <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <img src={loading} alt="Loading..." style={{ width: 28, height: 28 }} />
                        <span className="text-muted">Loading components…</span>
                    </div>
                ) : rowsCnt === 0 ? (
                    <div className="text-muted small">No components found for this employee for the selected month/year.</div>
                ) : (
                    <div className="table-responsive">
                        <table style={tableS} className="mb-0">
                            <thead>
                                <tr>
                                    <th colSpan={3} style={thGroup}>Earnings</th>
                                    <th colSpan={3} style={{ ...thGroup, ...sep }}>Deductions</th>
                                </tr>
                                <tr>
                                    <th style={thS}>Component</th>
                                    <th className="text-end" style={thS}>Amount</th>
                                    <th style={{ ...thS, ...actS }}></th>
                                    <th style={{ ...thS, ...sep }}>Component</th>
                                    <th className="text-end" style={thS}>Amount</th>
                                    <th style={{ ...thS, ...actS }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {Array.from({ length: rowsCnt }).map((_, i) => (
                                    <tr key={i}>
                                        {renderCells(E[i], 0)}
                                        {renderCells(D[i], 1)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    const getDetailPanelHeight = () => 180;
    const onDetailPanelToggled = async (params) => {
        try {
            const row = params?.row || {};
            const empId = row.emp_id;
            if (!empId) return;

            setLoadingExpanded((s) => ({ ...s, [empId]: true }));

            const { combined } = await fetchEmpDetailsForMonthYear(empId, selectedMonth, selectedYear);

            setComponentsByEmp((prev) => ({ ...prev, [empId]: combined }));
        } catch (err) {
            console.error('Failed to load expanded details:', err);
            toast.error('Failed to load employee details.');
        } finally {
            const row = params?.row || {};
            const empId = row.emp_id;
            setLoadingExpanded((s) => ({ ...s, [empId]: false }));
        }
    };

    const rows = useMemo(() => {
        const list = filteredRecords || [];
        return list.map((r) => ({
            payroll_schedule_id:
                r.payroll_schedule_id ??
                r.employee_salary_payroll_id ??
                `${r.emp_id || 'emp'}-${selectedYear}-${selectedMonth}`,
            ...r
        }));
    }, [filteredRecords, selectedMonth, selectedYear]);

    const getRowId = (row) =>
        row.payroll_schedule_id ||
        row.employee_salary_payroll_id ||
        `${row.emp_id || 'emp'}-${selectedYear}-${selectedMonth}`;

    const handleReset = async (e) => {
        e.preventDefault();
        setForm({
            emp_name: "",
        });
        setShowFilterModal(true);

        fetchPayrollDataByMonthYear();
    };

    return (
        <div className="pageBody">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px", flex: "0 0 auto" }}>
                        <h6 className="commonTableTitle" style={{ margin: 0 }}>Employee Payroll</h6>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: "10px", flex: "0 0 auto" }}>
                        <Button
                            variant="primary"
                            className="btn-success primaryBtn"
                            disabled={
                                !isTodayProcessingDate ||
                                !isSelectedMonthYearToday ||
                                isProcessing ||
                                isPayrollAlreadyDoneForMonth ||
                                !selectedMonth ||
                                !selectedYear ||
                                !derivedAcademicYearId
                            }
                            onClick={() => {
                                if (isPayrollAlreadyDoneForMonth) {
                                    toast.info("Payroll has already been processed for this month.");
                                    return;
                                }
                                handleRunPayroll();
                            }}
                            style={{
                                padding: "8px 16px",
                                fontSize: "14px",
                                fontWeight: "500"
                            }}
                        >
                            {isProcessing ? 'Processing...' : 'Run Payroll'}
                        </Button>

                        <OverlayTrigger placement="top" overlay={<RbTooltip id="tooltip-top">Filter</RbTooltip>}>
                            <Button
                                className="secondaryBtn"
                                variant="secondary"
                                onClick={handleShowFilterModal}
                                style={{ padding: "8px 12px" }}
                            >
                                <MdFilterList size={18} />
                            </Button>
                        </OverlayTrigger>
                    </div>
                </div>
                <div
                    className="mt-2 d-flex align-items-center w-100"
                    style={{ gap: "10px" }}
                >
                    <Form.Select
                        style={{
                            width: "120px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                        }}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(e.target.value)}
                    >
                        <option value="">Year</option>
                        {Array.from({ length: 5 }, (_, i) => {
                            const year = new Date().getFullYear() - 2 + i;
                            return (
                                <option key={year} value={year}>
                                    {year}
                                </option>
                            );
                        })}
                    </Form.Select>
                    <Form.Select
                        style={{
                            width: "140px",
                            fontSize: "14px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                        }}
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                        <option value="">Month</option>
                        {monthList.map((month) => (
                            <option key={month.month_id} value={month.month_id}>
                                {month.month_name}
                            </option>
                        ))}
                    </Form.Select>
                    <Button
                        variant="outline-success"
                        className="ms-auto d-flex align-items-center"
                        onClick={handleDownloadExcel}
                        disabled={!payrollData || payrollData.length === 0}
                        title="Download Excel"
                        style={{
                            padding: "8px 12px",
                            fontSize: "14px",
                            border: "1px solid #28a745",
                            color: "#28a745",
                        }}
                    >
                        <MdDownload size={16} />
                    </Button>
                </div>
            </div>
            <div className="commonTable height100" style={{ minHeight: "400px", marginTop: "60px" }}>
                <div className="tableBody">
                    {(isLoading || !hasLoadedMonthData) ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            rows={Array.isArray(rows) ? rows : []} 
                            columns={columns}
                            getRowId={getRowId}
                            showToolbar={false}
                            pagination
                            pageSizeOptions={[10, 20, 50]}
                            density="compact"
                            maxHeight={550}
                            renderDetailPanel={renderDetailPanel}
                            getDetailPanelHeight={getDetailPanelHeight}
                            onRowExpansionChange={onDetailPanelToggled}
                        />
                    )}
                </div>
            </div>

            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Row>
                        <Col xs={12}>
                            <div className='commonInput'>
                                <Form.Group controlId="emp_id">
                                    <Form.Label>Employee Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="emp_name"
                                        name="emp_name"
                                        placeholder="Enter Employee Name"
                                        value={filter.emp_id}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setFilter({ ...filter, emp_id: value });
                                        }}
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>  Reset    </Button>
                    <div>
                        <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showEmployeeModal}
                onHide={handleCloseEmployeeModal}
                size="xl"
                centered
                backdrop="static"
            >
                <Modal.Header closeButton style={{ borderBottom: "1px solid #dee2e6", padding: "20px 24px" }}>
                    <Modal.Title style={{ fontSize: "18px", fontWeight: "600" }}>
                        {selectedEmployee ? `Add Component for ${selectedEmployee.emp_name}` : 'Add Component'}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: "24px", maxHeight: "70vh", overflow: "hidden" }}>
                    <div style={{
                        backgroundColor: "#f8f9fa",
                        padding: "20px",
                        borderRadius: "8px",
                        marginBottom: "24px",
                        border: "1px solid #e9ecef"
                    }}>
                        <h6 className="mb-3" style={{ fontSize: "16px", fontWeight: "600", color: "#495057" }}>
                            Add New Component
                        </h6>

                        <Row className="g-3 align-items-end">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: "13px", fontWeight: 500, marginBottom: 6 }}>
                                        Component Name
                                    </Form.Label>
                                    <Form.Select
                                        size="sm"
                                        style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: 4 }}
                                        value={selectedSalaryComp.component_id}
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            const selectedComponent = salaryComponents.find(
                                                (c) => String(c.salary_component_id ?? c.component_id ?? c.id) === selectedId
                                            );
                                            const component_type_id = selectedComponent?.component_type_id || "";
                                            setSelectedSalaryComp((s) => ({ ...s, component_id: selectedId }));
                                            setComponentForm((f) => ({
                                                ...f,
                                                salary_component_id: selectedId,
                                                component_type_id: component_type_id
                                            }));
                                        }}
                                    >
                                        <option value="">Select Component</option>
                                        {(salaryComponents || []).map((c) => {
                                            const id = c.salary_component_id ?? c.component_id ?? c.id;
                                            const name = c.component_name ?? c.salary_component_name ?? c.name ?? c.component_code ?? "Component";
                                            const type = String(c.component_type_name || "").toUpperCase();
                                            return (
                                                <option key={id} value={id}>
                                                    {name}{type ? ` (${type})` : ""}
                                                </option>
                                            );
                                        })}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: "13px", fontWeight: 500, marginBottom: 6 }}>
                                        Amount (₹)
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="number"
                                        name="amount"
                                        placeholder="Enter amount"
                                        value={componentForm.amount}
                                        onChange={handleComponentFormChange}
                                        min="0"
                                        step="0.01"
                                        style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: 4 }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label style={{ fontSize: "13px", fontWeight: 500, marginBottom: 6 }}>
                                        Reason
                                    </Form.Label>
                                    <Form.Control
                                        size="sm"
                                        type="text"
                                        name="reason"
                                        placeholder="Add reason"
                                        value={componentForm.reason}
                                        onChange={handleComponentFormChange}
                                        style={{ padding: "6px 8px", fontSize: "13px", border: "1px solid #ddd", borderRadius: 4 }}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2} className="d-flex justify-content-end">
                                <Button
                                    onClick={handleAddComponent}
                                    style={{
                                        padding: "6px 12px",
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        borderRadius: 4,
                                        height: 32,
                                        marginBottom: "6px"
                                    }}
                                >
                                    <MdAdd size={16} className="me-1" /> Add
                                </Button>
                            </Col>
                        </Row>
                    </div>
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0" style={{ fontSize: "16px", fontWeight: "600", color: "#495057" }}>
                                Components
                            </h6>
                        </div>
                        <div style={{
                            maxHeight: "300px",
                            overflowY: "auto",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px"
                        }}>
                            {isLoadingComponents ? (
                                <div className="text-center p-4">
                                    <img src={loading} alt="Loading..." className="loadingGif" style={{ width: '40px', height: '40px' }} />
                                    <p className="mt-2 mb-0 text-muted">Loading components...</p>
                                </div>
                            ) : (
                                (() => {
                                    const all = employeeComponents || [];
                                    const E = all.filter((it) => String(it.component_type_name).toUpperCase() === "EARNING");
                                    const D = all.filter((it) => String(it.component_type_name).toUpperCase() === "DEDUCTION");
                                    const rowsCnt = Math.max(E.length, D.length);

                                    if (rowsCnt === 0) {
                                        return (
                                            <div className="p-3 text-muted small text-center">
                                                No components added for this employee.
                                            </div>
                                        );
                                    }

                                    const tableS = { width: "100%", borderCollapse: "collapse" };
                                    const thS = { padding: "0.4rem 0.6rem", fontSize: "0.85rem", fontWeight: 600, border: 0 };
                                    const thGroup = {
                                        ...thS,
                                        background: "#0d6efd",
                                        color: "#fff",
                                        textAlign: "center",
                                        borderRadius: 4,
                                    };
                                    const tdS = { padding: "0.4rem 0.6rem", fontSize: "0.85rem", border: 0 };
                                    const actS = { width: 64, minWidth: 64, whiteSpace: "nowrap" };
                                    const sep = { borderLeft: "1px solid var(--bs-border-color, #dee2e6)" };

                                    const renderCells = (item, sideIdx) => {
                                        const leftCellStyle = { ...tdS, ...(sideIdx > 0 ? sep : {}) };

                                        if (!item) {
                                            return (
                                                <>
                                                    <td style={leftCellStyle}></td>
                                                    <td className="text-end" style={tdS}></td>
                                                    <td className="text-end" style={{ ...tdS, ...actS }}></td>
                                                </>
                                            );
                                        }

                                        const label = item.salary_component_name || item.component_name || "Component";
                                        const isDeletingThis = deletingCompId === item.id;

                                        return (
                                            <>
                                                <td style={leftCellStyle}>{label}</td>
                                                <td className="text-end" style={tdS}>{fmtAmt(item.amount)}</td>
                                                <td style={{ ...tdS, ...actS }}>
                                                    <span className="d-inline-flex align-items-center gap-1 float-end">
                                                        <Button
                                                            size="sm"
                                                            variant="link"
                                                            className="p-0 text-danger"
                                                            onClick={() => handleDeleteComponent(item.id)}
                                                            title="Delete"
                                                            disabled={isDeletingThis}
                                                        >
                                                            {isDeletingThis ? <Spinner animation="border" size="sm" /> : <MdDelete />}
                                                        </Button>
                                                    </span>
                                                </td>
                                            </>
                                        );
                                    };

                                    return (
                                        <div className="table-responsive">
                                            <table style={tableS} className="mb-0">
                                                <thead>
                                                    <tr>
                                                        <th colSpan={3} style={thGroup}>Earnings</th>
                                                        <th colSpan={3} style={{ ...thGroup, ...sep }}>Deductions</th>
                                                    </tr>
                                                    <tr>
                                                        <th style={thS}>Component</th>
                                                        <th className="text-end" style={thS}>Amount</th>
                                                        <th style={{ ...thS, ...actS }}></th>
                                                        <th style={{ ...thS, ...sep }}>Component</th>
                                                        <th className="text-end" style={thS}>Amount</th>
                                                        <th style={{ ...thS, ...actS }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Array.from({ length: rowsCnt }).map((_, i) => (
                                                        <tr key={i}>
                                                            {renderCells(E[i], 0)}
                                                            {renderCells(D[i], 1)}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    );
                                })()
                            )}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: "1px solid #dee2e6", padding: "16px 24px" }}>
                    <Button
                        variant="secondary"
                        onClick={handleCloseEmployeeModal}
                        style={{ padding: "8px 20px", fontSize: "14px" }}
                    >
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showViewModal}
                onHide={handleCloseViewModal}
                size="lg"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {viewModal.emp
                            ? `Components — ${viewModal.emp.emp_name} (${getMonthName(selectedMonth)} ${selectedYear})`
                            : "Components"}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {viewModal.loading ? (
                        <div className="text-center p-4">
                            <img src={loading} alt="Loading..." style={{ width: 40, height: 40 }} />
                            <p className="mt-2 mb-0 text-muted">Loading components…</p>
                        </div>
                    ) : (() => {
                        const E = viewModal.earnings || [];
                        const D = viewModal.deductions || [];
                        const rowsCnt = Math.max(E.length, D.length);

                        if (rowsCnt === 0) {
                            return <div className="text-muted small">No components for the selected month/year.</div>;
                        }

                        const tableS = { width: "100%", borderCollapse: "collapse" };
                        const thS = { padding: "0.4rem 0.6rem", fontSize: "0.85rem", fontWeight: 600, border: 0 };
                        const thGroup = {
                            ...thS, background: "#0d6efd", color: "#fff", textAlign: "center", borderRadius: 4,
                        };
                        const tdS = { padding: "0.4rem 0.6rem", fontSize: "0.85rem", border: 0 };
                        const actS = { width: 64, minWidth: 64, whiteSpace: "nowrap" };
                        const sep = { borderLeft: "1px solid var(--bs-border-color, #dee2e6)" };

                        const renderCells = (item, sideIdx) => {
                            const leftCellStyle = { ...tdS, ...(sideIdx > 0 ? sep : {}) };
                            if (!item) {
                                return (
                                    <>
                                        <td style={leftCellStyle}></td>
                                        <td className="text-end" style={tdS}></td>
                                        <td style={{ ...tdS, ...actS }}></td>
                                    </>
                                );
                            }
                            const label = item.salary_component_name || item.component_name || "Component";
                            return (
                                <>
                                    <td style={leftCellStyle}>{label}</td>
                                    <td className="text-end" style={tdS}>{fmtAmt(item.amount)}</td>
                                    <td style={{ ...tdS, ...actS }}></td>
                                </>
                            );
                        };

                        return (
                            <div className="table-responsive">
                                <table style={tableS} className="mb-0">
                                    <thead>
                                        <tr>
                                            <th colSpan={3} style={thGroup}>Earnings</th>
                                            <th colSpan={3} style={{ ...thGroup, ...sep }}>Deductions</th>
                                        </tr>
                                        <tr>
                                            <th style={thS}>Component</th>
                                            <th className="text-end" style={thS}>Amount</th>
                                            <th style={{ ...thS, ...actS }}></th>
                                            <th style={{ ...thS, ...sep }}>Component</th>
                                            <th className="text-end" style={thS}>Amount</th>
                                            <th style={{ ...thS, ...actS }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: rowsCnt }).map((_, i) => (
                                            <tr key={i}>
                                                {renderCells(E[i], 0)}
                                                {renderCells(D[i], 1)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseViewModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};
export default ViewPayroll;
